import { ROUTES } from '../../app.constants'

export interface SearchItem {
  id: string
  name: string
  keywords: string
  section: string
  href: string
  scope: 'brand' | 'site' | 'both'
}

const buildSitePath = (path: string, siteId?: string): string => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  if (!siteId) {
    return `/location-selection?redirect=${encodeURIComponent(cleanPath)}`
  }
  return `/s/${siteId}/${cleanPath}`
}

const buildCorporatePath = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  return `/c/${cleanPath}`
}

const buildReportPath = (report: string, siteId?: string): string => {
  return siteId
    ? buildSitePath(`reports?report=${report}`, siteId)
    : buildCorporatePath(`reports?report=${report}`)
}

const buildAnalyticsPath = (analyticsTab: string, siteId?: string): string => {
  return siteId
    ? buildSitePath(`reports?tab=bi&analyticsTab=${analyticsTab}`, siteId)
    : buildCorporatePath(`reports?tab=bi&analyticsTab=${analyticsTab}`)
}

/**
 * Returns the searchable items list for the command palette / search overlay.
 *
 * NOTE on i18n: The `name`, `keywords`, and `section` fields are used as
 * programmatic identifiers for fuzzy-matching, not displayed to end-users in
 * a way that requires translation. Section names map to `SECTION_ICON_MAP` keys
 * in search.component.tsx. The visible text is already localised at the
 * component level via `useTranslation()`. Translating these slug-like values
 * would break the icon mapping and keyword matching without adding user value.
 */
export function getSearchableItems(siteId?: string): SearchItem[] {
  const locSettingsBase = ROUTES.settings.children.locationSettings.path
  const schedSettingsBase = ROUTES.settings.children.schedulerSettings.path
  const retSettingsBase = ROUTES.settings.children.retailSettings.path
  const accSettingsBase = ROUTES.settings.children.accountSettings.path

  const items: SearchItem[] = [
    // === Features (both contexts) ===
    {
      id: 'feat-dashboard',
      name: 'Dashboard',
      keywords: 'dashboard home overview welcome',
      section: 'Features',
      href: siteId
        ? buildSitePath(ROUTES.dashboard.path, siteId)
        : buildCorporatePath(ROUTES.dashboard.path),
      scope: 'both',
    },
    {
      id: 'feat-reports',
      name: 'Reports',
      keywords: 'reports analytics data exports metrics',
      section: 'Features',
      href: siteId
        ? buildSitePath(ROUTES.reports.path, siteId)
        : buildCorporatePath(ROUTES.reports.path),
      scope: 'both',
    },
    {
      id: 'feat-help-docs',
      name: 'Help Docs',
      keywords: 'help documentation articles knowledge base support',
      section: 'Features',
      href: siteId
        ? buildSitePath(ROUTES.helpDocs.path, siteId)
        : buildCorporatePath(ROUTES.helpDocs.path),
      scope: 'both',
    },

    // === Site-only Features ===
    {
      id: 'feat-schedule',
      name: 'Schedule',
      keywords: 'schedule calendar classes sessions timetable',
      section: 'Features',
      href: buildSitePath(ROUTES.schedule.path, siteId),
      scope: 'site',
    },
    {
      id: 'feat-clients',
      name: 'Clients',
      keywords: 'clients members users customers directory',
      section: 'Features',
      href: buildSitePath(ROUTES.clients.path, siteId),
      scope: 'site',
    },
    {
      id: 'feat-payments',
      name: 'Payments',
      keywords: 'payments retail billing transactions',
      section: 'Features',
      href: buildSitePath(ROUTES.retail.path, siteId),
      scope: 'site',
    },

    // === Brand Management ===
    {
      id: 'brand-control-center',
      name: 'Control Center',
      keywords: 'control center brand administration hub',
      section: 'Brand Management',
      href: buildCorporatePath(ROUTES.controlCenter.path),
      scope: 'brand',
    },
    {
      id: 'brand-communication-templates',
      name: 'Communication Templates',
      keywords: 'communication templates email sms messages',
      section: 'Brand Management',
      href: buildCorporatePath(ROUTES.controlCenter.children.communicationTemplates.path),
      scope: 'brand',
    },
    {
      id: 'brand-faqs',
      name: 'FAQs',
      keywords: 'faqs frequently asked questions help',
      section: 'Brand Management',
      href: buildCorporatePath(ROUTES.controlCenter.children.faqs.path),
      scope: 'brand',
    },
    {
      id: 'brand-api-integrations',
      name: 'API Integrations',
      keywords: 'api integrations webhooks zapier third party',
      section: 'Brand Management',
      href: buildCorporatePath(ROUTES.controlCenter.children.apiIntegrations.path),
      scope: 'brand',
    },
    {
      id: 'brand-audit-history',
      name: 'Audit History',
      keywords: 'audit history log tracking changes compliance',
      section: 'Brand Management',
      href: buildCorporatePath(ROUTES.controlCenter.children.auditHistory.path),
      scope: 'brand',
    },
    {
      id: 'brand-mobile-setup',
      name: 'Mobile App Setup',
      keywords: 'mobile app setup customization branding self service',
      section: 'Brand Management',
      href: buildCorporatePath(ROUTES.controlCenter.children.selfService.path),
      scope: 'brand',
    },

    // === User & Team Access ===
    {
      id: 'user-access',
      name: 'User Access',
      keywords: 'user access permissions roles staff admin',
      section: 'User & Team Access',
      href: buildCorporatePath(ROUTES.userAccess.path),
      scope: 'brand',
    },
    {
      id: 'user-access-policies',
      name: 'Access Policies',
      keywords: 'access policies permissions roles groups',
      section: 'User & Team Access',
      href: buildCorporatePath(`${ROUTES.userAccess.path}/access-policies`),
      scope: 'brand',
    },
    {
      id: 'user-location-groups',
      name: 'Location Groups',
      keywords: 'location groups access permissions site',
      section: 'User & Team Access',
      href: buildCorporatePath(`${ROUTES.userAccess.path}/location-groups`),
      scope: 'brand',
    },
    {
      id: 'team-access',
      name: 'Team Access',
      keywords: 'team access site staff permissions location',
      section: 'User & Team Access',
      href: buildSitePath(ROUTES.siteTeamAccess.path, siteId),
      scope: 'site',
    },

    // === Members (site-level) ===
    {
      id: 'members-directory',
      name: 'Client Directory',
      keywords: 'client directory members list browse all',
      section: 'Members',
      href: buildSitePath(ROUTES.clients.path, siteId),
      scope: 'site',
    },
    {
      id: 'members-add',
      name: 'Add Client',
      keywords: 'add client new member create register',
      section: 'Members',
      href: buildSitePath(ROUTES.clients.path, siteId),
      scope: 'site',
    },
    {
      id: 'members-corporate-lookup',
      name: 'Corporate Lookup',
      keywords: 'corporate lookup business clients search',
      section: 'Members',
      href: buildSitePath('clients/lookup', siteId),
      scope: 'site',
    },

    // === Payments (site-level) ===
    {
      id: 'pay-payment-log',
      name: 'Payment Log',
      keywords: 'payment log transactions history',
      section: 'Payments',
      href: buildSitePath('retail/payment-log', siteId),
      scope: 'site',
    },
    {
      id: 'pay-memberships',
      name: 'Memberships',
      keywords: 'memberships subscriptions recurring billing',
      section: 'Payments',
      href: buildSitePath('retail/memberships', siteId),
      scope: 'site',
    },
    {
      id: 'pay-packages',
      name: 'Packages',
      keywords: 'packages service bundles pricing',
      section: 'Payments',
      href: buildSitePath('retail/packages', siteId),
      scope: 'site',
    },
    {
      id: 'pay-pos',
      name: 'POS',
      keywords: 'pos point of sale inventory products',
      section: 'Payments',
      href: buildSitePath('retail/pos', siteId),
      scope: 'site',
    },
    {
      id: 'pay-gift-cards',
      name: 'Gift Cards',
      keywords: 'gift cards vouchers redemption',
      section: 'Payments',
      href: buildSitePath('retail/gift-card', siteId),
      scope: 'site',
    },
    {
      id: 'pay-disputes',
      name: 'Disputes',
      keywords: 'disputes chargebacks payment issues',
      section: 'Payments',
      href: buildSitePath('retail/disputes', siteId),
      scope: 'site',
    },
    {
      id: 'pay-new-sale',
      name: 'New Sale',
      keywords: 'new sale create transaction manual',
      section: 'Payments',
      href: buildSitePath(ROUTES.retailDashboard.children.newSale.path, siteId),
      scope: 'site',
    },
    {
      id: 'pay-failed',
      name: 'Failed Payments',
      keywords: 'failed payments retry declined',
      section: 'Payments',
      href: buildSitePath(ROUTES.retailDashboard.children.failed.path, siteId),
      scope: 'site',
    },
    {
      id: 'pay-draft-invoices',
      name: 'Draft Invoices',
      keywords: 'draft invoices pending unpaid',
      section: 'Payments',
      href: buildSitePath(ROUTES.retailDashboard.children.draftInvoices.path, siteId),
      scope: 'site',
    },
    {
      id: 'pay-scheduled',
      name: 'Scheduled Payments',
      keywords: 'scheduled payments upcoming recurring',
      section: 'Payments',
      href: buildSitePath('retail/scheduled', siteId),
      scope: 'site',
    },
    {
      id: 'pay-payasyougo',
      name: 'Pay As You Go',
      keywords: 'pay as you go drop in casual',
      section: 'Payments',
      href: buildSitePath('retail/pay-as-you-go', siteId),
      scope: 'site',
    },

    // === Reports — Exports ===
    {
      id: 'rpt-exp-all-clients',
      name: 'Exports - All Clients',
      keywords: 'report export all clients members list',
      section: 'Reports',
      href: buildReportPath('all_clients', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-client-checkin',
      name: 'Exports - Client Check-in',
      keywords: 'report export client check-in attendance',
      section: 'Reports',
      href: buildReportPath('client_checkin', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-late-cancels',
      name: 'Exports - Late Cancels',
      keywords: 'report export late cancels cancellation',
      section: 'Reports',
      href: buildReportPath('late_cancels', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-no-shows',
      name: 'Exports - No Shows',
      keywords: 'report export no shows absent missed',
      section: 'Reports',
      href: buildReportPath('no_shows', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-first-visits',
      name: 'Exports - First Visits',
      keywords: 'report export first visits new member trial',
      section: 'Reports',
      href: buildReportPath('first_visits', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-clients-absent',
      name: 'Exports - Clients Absent',
      keywords: 'report export clients absent inactive missing',
      section: 'Reports',
      href: buildReportPath('clients_absent', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-referrals',
      name: 'Exports - Referrals',
      keywords: 'report export referrals referral source',
      section: 'Reports',
      href: buildReportPath('referrals', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-session-credits-remaining',
      name: 'Exports - Session Credits Remaining',
      keywords: 'report export session credits remaining balance',
      section: 'Reports',
      href: buildReportPath('session_credits_remaining', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-membership-details',
      name: 'Exports - Membership Details',
      keywords: 'report export membership details subscriptions active',
      section: 'Reports',
      href: buildReportPath('membership_details', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-revenue-used',
      name: 'Exports - Revenue Used',
      keywords: 'report export revenue used earned recognised',
      section: 'Reports',
      href: buildReportPath('revenue_used', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-net-revenue',
      name: 'Exports - Net Revenue',
      keywords: 'report export net revenue income profit',
      section: 'Reports',
      href: buildReportPath('net_revenue', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-sales-by-service',
      name: 'Exports - Sales by Service',
      keywords: 'report export sales service breakdown',
      section: 'Reports',
      href: buildReportPath('sales_by_service', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-failed-payments',
      name: 'Exports - Failed Payments',
      keywords: 'report export failed payments declined',
      section: 'Reports',
      href: buildReportPath('failed_payments', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-failed-payment-history',
      name: 'Exports - Failed Payment History',
      keywords: 'report export failed payment history retry',
      section: 'Reports',
      href: buildReportPath('failed_payment_history', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-suspended-memberships',
      name: 'Exports - Suspended Memberships',
      keywords: 'report export suspended memberships frozen hold',
      section: 'Reports',
      href: buildReportPath('suspended_memberships', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-stripe-settlement',
      name: 'Exports - Stripe Settlement',
      keywords: 'report export stripe settlement payout',
      section: 'Reports',
      href: buildReportPath('stripe_settlement', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-cancellation-report',
      name: 'Exports - Cancellation Report',
      keywords: 'report export cancellation reasons churn',
      section: 'Reports',
      href: buildReportPath('cancellation_report', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-projected-revenue',
      name: 'Exports - Projected Revenue',
      keywords: 'report export projected revenue forecast',
      section: 'Reports',
      href: buildReportPath('projected_revenue', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-royalty-fee-distribution',
      name: 'Exports - Royalty Fee Distribution',
      keywords: 'report export royalty fee distribution franchise',
      section: 'Reports',
      href: buildReportPath('royalty_fee_distribution', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-income-handover',
      name: 'Exports - Income Handover',
      keywords: 'report export income handover cash register',
      section: 'Reports',
      href: buildReportPath('income_handover', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-delinquency',
      name: 'Exports - Delinquency',
      keywords: 'report export delinquency overdue past due',
      section: 'Reports',
      href: buildReportPath('delinquency', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-all-invoices',
      name: 'Exports - All Invoices',
      keywords: 'report export all invoices billing',
      section: 'Reports',
      href: buildReportPath('all_invoices', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-discount-usage',
      name: 'Exports - Discount Usage',
      keywords: 'report export discount usage promo',
      section: 'Reports',
      href: buildReportPath('discount_usage', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-pos-history',
      name: 'Exports - POS History',
      keywords: 'report export pos point of sale history retail',
      section: 'Reports',
      href: buildReportPath('pos_history', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-promo-code-usage',
      name: 'Exports - Promo Code Usage',
      keywords: 'report export promo code usage coupon',
      section: 'Reports',
      href: buildReportPath('promo_code_usage', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-expired-session-credits',
      name: 'Exports - Expired Session Credits',
      keywords: 'report export expired session credits lapsed',
      section: 'Reports',
      href: buildReportPath('expired_session_credits', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-stripe-card-updater',
      name: 'Exports - Stripe Card Updater',
      keywords: 'report export stripe card updater expiring',
      section: 'Reports',
      href: buildReportPath('stripe_card_updater', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-account-credit-balance',
      name: 'Exports - Account Credit Balance',
      keywords: 'report export account credit balance',
      section: 'Reports',
      href: buildReportPath('account_credit_balance', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-ezypay-settlement',
      name: 'Exports - Ezypay Settlement',
      keywords: 'report export ezypay settlement payout',
      section: 'Reports',
      href: buildReportPath('ezypay_settlement', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-gift-cards',
      name: 'Exports - Gift Cards',
      keywords: 'report export gift cards voucher',
      section: 'Reports',
      href: buildReportPath('gift_cards', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-current-inventory',
      name: 'Exports - Current Inventory',
      keywords: 'report export current inventory stock products',
      section: 'Reports',
      href: buildReportPath('current_inventory', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-birthdays',
      name: 'Exports - Birthdays',
      keywords: 'report export birthdays anniversary',
      section: 'Reports',
      href: buildReportPath('birthdays', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-session-feedback',
      name: 'Exports - Session Feedback',
      keywords: 'report export session feedback ratings reviews',
      section: 'Reports',
      href: buildReportPath('session_feedback', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-member-movement',
      name: 'Exports - Member Movement',
      keywords: 'report export member movement joins leaves churn',
      section: 'Reports',
      href: buildReportPath('member_movement', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-new-clients',
      name: 'Exports - New Clients',
      keywords: 'report export new clients signups registrations',
      section: 'Reports',
      href: buildReportPath('new_clients', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-memberships-expiring',
      name: 'Exports - Memberships Expiring',
      keywords: 'report export memberships expiring ending soon',
      section: 'Reports',
      href: buildReportPath('memberships_expiring', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-refund-history',
      name: 'Exports - Refund History',
      keywords: 'report export refund history returns',
      section: 'Reports',
      href: buildReportPath('refund_history', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-unpaid-sessions',
      name: 'Exports - Unpaid Sessions',
      keywords: 'report export unpaid sessions outstanding',
      section: 'Reports',
      href: buildReportPath('unpaid_sessions', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-amount-due',
      name: 'Exports - Amount Due',
      keywords: 'report export amount due outstanding balances',
      section: 'Reports',
      href: buildReportPath('amount_due', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-intro-offer-conversions',
      name: 'Exports - Intro Offer Conversions',
      keywords: 'report export intro offer conversions trial',
      section: 'Reports',
      href: buildReportPath('intro_offer_conversions', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-staff-utilisation',
      name: 'Exports - Staff Utilisation',
      keywords: 'report export staff utilisation instructor hours',
      section: 'Reports',
      href: buildReportPath('staff_utilisation', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-client-lifetime-value',
      name: 'Exports - Client Lifetime Value',
      keywords: 'report export client lifetime value CLV revenue',
      section: 'Reports',
      href: buildReportPath('client_lifetime_value', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-client-retention-rate',
      name: 'Exports - Client Retention Rate',
      keywords: 'report export client retention rate churn',
      section: 'Reports',
      href: buildReportPath('client_retention_rate', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-payroll',
      name: 'Exports - Payroll',
      keywords: 'report export payroll staff wages pay',
      section: 'Reports',
      href: buildReportPath('payroll', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-payroll-tier-audit',
      name: 'Exports - Payroll Tier Audit',
      keywords: 'report export payroll tier audit commission',
      section: 'Reports',
      href: buildReportPath('payroll_tier_audit', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-one-to-one-sessions',
      name: 'Exports - One to One Sessions',
      keywords: 'report export one to one sessions private personal training',
      section: 'Reports',
      href: buildReportPath('one_to_one_sessions', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-session-performance',
      name: 'Exports - Session Performance',
      keywords: 'report export session performance class attendance',
      section: 'Reports',
      href: buildReportPath('session_performance', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-tax-summary',
      name: 'Exports - Tax Summary',
      keywords: 'report export tax summary GST VAT',
      section: 'Reports',
      href: buildReportPath('tax_summary', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-revenue-leakage',
      name: 'Exports - Revenue Leakage',
      keywords: 'report export revenue leakage loss uncollected',
      section: 'Reports',
      href: buildReportPath('revenue_leakage', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-waitlist',
      name: 'Exports - Waitlist',
      keywords: 'report export waitlist queue demand',
      section: 'Reports',
      href: buildReportPath('waitlist', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-client-engagement',
      name: 'Exports - Client Engagement',
      keywords: 'report export client engagement activity frequency',
      section: 'Reports',
      href: buildReportPath('client_engagement', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-multi-location-comparison',
      name: 'Exports - Multi Location Comparison',
      keywords: 'report export multi location comparison benchmark',
      section: 'Reports',
      href: buildReportPath('multi_location_comparison', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-contract-agreement',
      name: 'Exports - Contract Agreement',
      keywords: 'report export contract agreement signed',
      section: 'Reports',
      href: buildReportPath('contract_agreement', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-exp-write-off-bad-debt',
      name: 'Exports - Write Off Bad Debt',
      keywords: 'report export write off bad debt uncollectable',
      section: 'Reports',
      href: buildReportPath('write_off_bad_debt', siteId),
      scope: 'both',
    },

    // === Reports — Analytics ===
    {
      id: 'rpt-bi-daily-snapshot',
      name: 'Analytics - Daily Snapshot',
      keywords: 'report analytics daily snapshot overview dashboard bi',
      section: 'Reports',
      href: buildAnalyticsPath('daily-snapshot', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-bi-checkin-analysis',
      name: 'Analytics - Check-In Analysis',
      keywords: 'report analytics check-in analysis attendance bi',
      section: 'Reports',
      href: buildAnalyticsPath('check-in-analysis', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-bi-revenue-analysis',
      name: 'Analytics - Revenue Analysis',
      keywords: 'report analytics revenue analysis income bi',
      section: 'Reports',
      href: buildAnalyticsPath('revenue-analysis', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-bi-class-performance',
      name: 'Analytics - Class Performance',
      keywords: 'report analytics class performance session attendance bi',
      section: 'Reports',
      href: buildAnalyticsPath('class-performance', siteId),
      scope: 'both',
    },
    {
      id: 'rpt-bi-sales-acquisition',
      name: 'Analytics - Sales & Acquisition',
      keywords: 'report analytics sales acquisition conversion bi',
      section: 'Reports',
      href: buildAnalyticsPath('sales-acquisition', siteId),
      scope: 'both',
    },

    // === Location Settings (site-level) — Top-level pages ===
    {
      id: 'loc-configuration',
      name: 'Location Configuration',
      keywords: 'location configuration studio info hours zones address phone',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-social-media',
      name: 'Social Media',
      keywords: 'social media facebook instagram twitter website links',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/social-media`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-checkin',
      name: 'Check-in Settings',
      keywords: 'check-in checkin contactless barcode refresh mobile',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/check-in`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-tools',
      name: 'Location Tools',
      keywords: 'location tools kiosk front desk barcode embed booking widgets',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-tools`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-integrations',
      name: 'Integrations',
      keywords: 'integrations door access crm accounting software third party',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/integrations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-operations',
      name: 'Location Operations',
      keywords: 'operations capacity rules policies signup cancellation lead sources',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-webhooks',
      name: 'Webhooks',
      keywords: 'webhooks outbound event notifications',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/webhooks`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-rooms',
      name: 'Rooms & Resources',
      keywords: 'rooms resources configuration equipment booking spaces',
      section: 'Location Settings',
      href: buildSitePath(
        ROUTES.settings.children.locationSettings.children.roomResourceConfiguration.path,
        siteId
      ),
      scope: 'site',
    },
    {
      id: 'loc-comm-templates',
      name: 'Communication Templates',
      keywords: 'communication templates email sms site override',
      section: 'Location Settings',
      href: buildSitePath(
        ROUTES.settings.children.locationSettings.children.siteCommunicationTemplates.path,
        siteId
      ),
      scope: 'site',
    },

    // --- Location Configuration: Business Customization ---
    {
      id: 'loc-cfg-business-name',
      name: 'Business Name',
      keywords: 'business name studio location title',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-business-description',
      name: 'Business Description',
      keywords: 'business description about mission statement bio',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-business-phone',
      name: 'Business Phone',
      keywords: 'business phone number contact telephone',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-business-email',
      name: 'Business Email',
      keywords: 'business email contact address',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-business-address',
      name: 'Business Address',
      keywords: 'business address street city state country postal zip',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-business-logo',
      name: 'Business Logo',
      keywords: 'business logo image branding upload',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-business-hours',
      name: 'Business Hours',
      keywords: 'business hours opening closing times schedule operating',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-revenue-period',
      name: 'Dashboard Revenue Period',
      keywords: 'dashboard revenue period calculation time range',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },

    // --- Location Configuration: Regional & Communication ---
    {
      id: 'loc-cfg-digital-channels',
      name: 'Digital Channels',
      keywords: 'digital channels whatsapp instagram messenger social media',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-currency',
      name: 'Currency',
      keywords: 'currency money format dollar euro pound',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-date-format',
      name: 'Date Format',
      keywords: 'date format MM DD YYYY display regional',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-time-format',
      name: 'Time Format',
      keywords: 'time format 12 hour 24 hour AM PM clock',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-timezone',
      name: 'Time Zone',
      keywords: 'timezone time zone region UTC offset',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },
    {
      id: 'loc-cfg-number-format',
      name: 'Number Format',
      keywords: 'number format decimal thousands separator regional',
      section: 'Location Settings',
      href: buildSitePath(locSettingsBase, siteId),
      scope: 'site',
    },

    // --- Location Operations: Individual Items ---
    {
      id: 'loc-ops-auto-logout',
      name: 'Auto Logout',
      keywords: 'auto logout timeout session expiry staff',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-enable-signup',
      name: 'Enable Clients to Sign Up',
      keywords: 'enable signup self registration client onboarding',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-signup-fields-business',
      name: 'Sign Up Fields - Business',
      keywords: 'signup fields business staff required form',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-signup-fields-client',
      name: 'Sign Up Fields - Client',
      keywords: 'signup fields client required form member',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-custom-properties',
      name: 'Custom Properties',
      keywords: 'custom properties fields data attributes metadata',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-cancellation-reasons',
      name: 'Cancellation Reasons',
      keywords: 'cancellation reasons predefined options booking cancel',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-policies',
      name: 'Policies',
      keywords: 'policies agreements terms conditions acceptance waiver',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-suspension-reasons',
      name: 'Suspension Reasons',
      keywords: 'suspension reasons hold freeze membership',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-questionnaire-forms',
      name: 'Questionnaire Forms',
      keywords: 'questionnaire forms intake survey custom booking',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-default-business-tab',
      name: 'Default Business Profile Tab',
      keywords: 'default business profile tab homepage staff',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-ops-default-client-tab',
      name: 'Default Client Tab',
      keywords: 'default client tab homepage member',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/location-operations`, siteId),
      scope: 'site',
    },

    // --- Check-in: Individual Items ---
    {
      id: 'loc-chk-general',
      name: 'General Check-in',
      keywords: 'general check-in enable staff attendance',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/check-in`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-chk-barcode',
      name: 'Barcode Check-in',
      keywords: 'barcode check-in scanner scanning refresh period',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/check-in`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-chk-contactless',
      name: 'Contactless Check-in',
      keywords: 'contactless check-in QR code touchless',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/check-in`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-chk-mobile',
      name: 'Mobile Client Check-in',
      keywords: 'mobile client check-in self app',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/check-in`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-chk-popups',
      name: 'Check-in Popups',
      keywords: 'check-in popups confirmation dialog alert',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/check-in`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-chk-widget',
      name: 'Check-in Widget',
      keywords: 'check-in widget display embed',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/check-in`, siteId),
      scope: 'site',
    },
    {
      id: 'loc-chk-sound-alerts',
      name: 'Sound Alerts',
      keywords: 'sound alerts audio notification check-in beep',
      section: 'Location Settings',
      href: buildSitePath(`${locSettingsBase}/check-in`, siteId),
      scope: 'site',
    },

    // === Scheduler Settings (site-level) ===
    {
      id: 'sched-session-types',
      name: 'Session Types',
      keywords: 'session types class categories colors pricing',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/session-types`, siteId),
      scope: 'site',
    },
    {
      id: 'sched-templates',
      name: 'Schedule Templates',
      keywords: 'schedule templates recurring session definitions',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/schedule-templates`, siteId),
      scope: 'site',
    },
    {
      id: 'sched-calendar',
      name: 'Calendar Configuration',
      keywords: 'calendar configuration business hours holidays availability',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/calendar-configurations`, siteId),
      scope: 'site',
    },
    {
      id: 'sched-rules',
      name: 'Scheduler Rules',
      keywords: 'scheduler rules booking cancellation waitlist thresholds',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/scheduler-rules`, siteId),
      scope: 'site',
    },
    {
      id: 'sched-rules-booking',
      name: 'Booking Rules',
      keywords: 'booking rules advance limit minimum maximum lead time',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/scheduler-rules`, siteId),
      scope: 'site',
    },
    {
      id: 'sched-rules-cancellation',
      name: 'Cancellation Rules',
      keywords: 'cancellation rules policy deadline penalty late cancel fee',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/scheduler-rules`, siteId),
      scope: 'site',
    },
    {
      id: 'sched-rules-class',
      name: 'Class Rules',
      keywords: 'class rules session capacity limits group size',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/scheduler-rules`, siteId),
      scope: 'site',
    },
    {
      id: 'sched-rules-reservation',
      name: 'Reservation Rules',
      keywords: 'reservation rules spot booking hold release',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/scheduler-rules`, siteId),
      scope: 'site',
    },
    {
      id: 'sched-rules-waitlist',
      name: 'Waitlist Rules',
      keywords: 'waitlist rules queue promotion auto convert',
      section: 'Scheduler Settings',
      href: buildSitePath(`${schedSettingsBase}/scheduler-rules`, siteId),
      scope: 'site',
    },

    // === Retail Settings (site-level) ===
    {
      id: 'ret-packages',
      name: 'Packages',
      keywords: 'packages service membership pricing configuration',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/packages`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-memberships',
      name: 'Memberships',
      keywords: 'memberships recurring subscription configuration',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/memberships`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-pos-inventory',
      name: 'POS Inventory',
      keywords: 'pos inventory product catalog stock retail',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/pos-inventory`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-promo-codes',
      name: 'Promo Codes',
      keywords: 'promo codes coupons discounts promotions',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/promo-codes`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-gift-cards',
      name: 'Gift Cards',
      keywords: 'gift cards design template rules configuration',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/gift-cards`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-accounts',
      name: 'Accounts',
      keywords: 'accounts payment gateway setup stripe ezypay',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/accounts`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-credits',
      name: 'Credits',
      keywords: 'credits system configuration balance',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/credits`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-fees',
      name: 'Retail Fees',
      keywords: 'retail fees transaction rules charges',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/retail-fees`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-sales',
      name: 'Sales',
      keywords: 'sales revenue tagging invoice settings',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/sales`, siteId),
      scope: 'site',
    },
    {
      id: 'ret-readers',
      name: 'Readers',
      keywords: 'readers payment terminal hardware devices',
      section: 'Retail Settings',
      href: buildSitePath(`${retSettingsBase}/readers`, siteId),
      scope: 'site',
    },

    // === Account Settings (site-level) ===
    {
      id: 'acc-billing',
      name: 'Billing',
      keywords: 'billing invoice recipients payment defaults',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/billing`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-tax',
      name: 'Tax Settings',
      keywords: 'tax settings calculation rules rates',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/tax-settings`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-invoice',
      name: 'Invoice Settings',
      keywords: 'invoice settings branding numbering defaults',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/invoice-settings`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-pos',
      name: 'Point of Sale',
      keywords: 'point of sale pos payment methods tipping receipts',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/point-of-sale`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-payroll',
      name: 'Payroll Configurations',
      keywords: 'payroll configurations staff pay tiers commission rates',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/payroll-configurations`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-sms',
      name: 'SMS Credits',
      keywords: 'sms credits messaging packages balance text',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/sms-credits`, siteId),
      scope: 'site',
    },
    // --- Account Settings: Individual Items ---
    {
      id: 'acc-billing-retry',
      name: 'Retry Failed Payment',
      keywords: 'retry failed payment automatic billing dunning',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/billing`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-billing-transaction-fees',
      name: 'Transaction Fees',
      keywords: 'transaction fees surcharge processing billing',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/billing`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-invoice-business-number',
      name: 'Invoice Business Number',
      keywords: 'invoice business number ABN tax ID',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/invoice-settings`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-invoice-email',
      name: 'Invoice Email',
      keywords: 'invoice email sender address notification',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/invoice-settings`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-pos-cash-drawer',
      name: 'Cash Drawer',
      keywords: 'cash drawer register float open close',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/point-of-sale`, siteId),
      scope: 'site',
    },
    {
      id: 'acc-pos-sale-item-types',
      name: 'Sale Item Types',
      keywords: 'sale item types categories products services POS',
      section: 'Account Settings',
      href: buildSitePath(`${accSettingsBase}/point-of-sale`, siteId),
      scope: 'site',
    },
  ]

  // Filter by scope
  if (siteId) {
    return items // Show all items when at site level
  }
  return items.filter((item) => item.scope === 'brand' || item.scope === 'both')
}

// Section display order
export const SECTION_ORDER = [
  'Features',
  'Reports',
  'Members',
  'Payments',
  'Scheduler Settings',
  'Location Settings',
  'Retail Settings',
  'Account Settings',
  'Brand Management',
  'User & Team Access',
]

// Map of section names to their SVG icon imports (resolved in component)
export const SECTION_ICONS: Record<string, string> = {
  Features: 'apps',
  Reports: 'chart-line',
  Members: 'people-group',
  Payments: 'credit-card-small',
  'Scheduler Settings': 'calendar',
  'Location Settings': 'setting',
  'Retail Settings': 'shopping-cart',
  'Account Settings': 'file-graph',
  'Brand Management': 'apps',
  'User & Team Access': 'key-skeleton',
}
